import { useEffect, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { useSession } from '../lib/session';
import type { Navigate } from '../lib/router';
import { color } from '../tokens';
import { AccentButton, GhostButton, Spinner, TextInput } from '../ui/primitives';

/**
 * Account recovery.
 *
 * Two screens sharing one card: asking for a link, and choosing a new password
 * once you have one. The request step deliberately gives the same confirmation
 * whether or not the address is registered — anything else lets a stranger test
 * which emails have accounts here.
 */

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div
        style={{
          width: 372,
          background: color.surface,
          border: `1px solid ${color.lineSoft}`,
          borderRadius: 18,
          padding: '30px 28px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Brand({ accent }: { accent: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
      <div style={{ width: 18, height: 18, borderRadius: 5, background: accent }} />
      <div style={{ fontSize: 14, fontWeight: 700 }}>Scribbleplot</div>
    </div>
  );
}

export function ForgotScreen({ accent, navigate }: { accent: string; navigate: Navigate }) {
  const mailEnabled = useSession((s) => s.mailEnabled);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (busy || !email.trim()) return;
    setBusy(true);
    try {
      await api.forgotPassword(email);
    } catch {
      // The endpoint is intentionally uninformative; a failure here should not
      // reveal anything either.
    } finally {
      setBusy(false);
      setSent(true);
    }
  };

  if (sent) {
    return (
      <Card>
        <Brand accent={accent} />
        <div style={{ fontSize: 20, fontWeight: 700 }}>Check your email</div>
        <div style={{ fontSize: 13, color: color.textMuted, lineHeight: 1.6 }}>
          If <strong style={{ color: color.text }}>{email.trim()}</strong> has an account, a reset link is on its way.
          It expires in an hour.
        </div>
        {!mailEnabled && (
          <div
            style={{
              fontSize: 12,
              color: color.textDim,
              lineHeight: 1.6,
              background: color.input,
              border: `1px solid ${color.lineStrong}`,
              borderRadius: 9,
              padding: 11,
            }}
          >
            This instance has no mail server configured, so the link was written to the server log instead. Ask whoever
            runs it to send it to you.
          </div>
        )}
        <GhostButton onClick={() => navigate({ name: 'signin' })}>Back to sign in</GhostButton>
      </Card>
    );
  }

  return (
    <Card>
      <Brand accent={accent} />
      <div>
        <div style={{ fontSize: 20, fontWeight: 700 }}>Reset your password</div>
        <div style={{ fontSize: 13, color: color.textMuted, marginTop: 4 }}>
          We will send a link to choose a new one.
        </div>
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <TextInput
          value={email}
          onChange={setEmail}
          placeholder="you@company.com"
          type="email"
          name="email"
          autoComplete="email"
          autoFocus
        />
        <AccentButton
          accent={accent}
          type="submit"
          disabled={busy || !email.trim()}
          style={{ height: 38, width: '100%', borderRadius: 10, fontSize: 13.5 }}
        >
          {busy ? 'Sending…' : 'Send reset link'}
        </AccentButton>
      </form>

      <a
        href="/signin"
        onClick={(event) => {
          event.preventDefault();
          navigate({ name: 'signin' });
        }}
        style={{ fontSize: 12, textAlign: 'center', cursor: 'pointer', color: color.textFaint, textDecoration: 'none' }}
      >
        ← back to sign in
      </a>
    </Card>
  );
}

export function ResetScreen({ token, accent, navigate }: { token: string; accent: string; navigate: Navigate }) {
  const setUser = useSession((s) => s.setUser);
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [dead, setDead] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .checkReset(token)
      .then((r) => !cancelled && setEmail(r.email))
      .catch((err) => !cancelled && setDead(err instanceof ApiError ? err.message : 'This reset link is not valid.'));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async () => {
    if (busy) return;
    setError(null);
    if (password.length < 10) {
      setError('Passwords need at least 10 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Those two passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const { user } = await api.resetPassword(token, password);
      // The reset signs you in, so there is no second step.
      setUser(user);
      navigate({ name: 'home' }, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not set that password.');
      setBusy(false);
    }
  };

  if (dead) {
    return (
      <Card>
        <Brand accent={accent} />
        <div style={{ fontSize: 20, fontWeight: 700 }}>This link has expired</div>
        <div style={{ fontSize: 13, color: color.textMuted, lineHeight: 1.6 }}>{dead}</div>
        <AccentButton accent={accent} onClick={() => navigate({ name: 'forgot' })}>
          Ask for a new one
        </AccentButton>
      </Card>
    );
  }

  if (!email) return <Spinner accent={accent} label="Checking link" />;

  return (
    <Card>
      <Brand accent={accent} />
      <div>
        <div style={{ fontSize: 20, fontWeight: 700 }}>Choose a new password</div>
        <div style={{ fontSize: 13, color: color.textMuted, marginTop: 4 }}>for {email}</div>
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <TextInput
          value={password}
          onChange={setPassword}
          placeholder="New password — 10 characters or more"
          type="password"
          name="password"
          autoComplete="new-password"
          autoFocus
        />
        <TextInput
          value={confirm}
          onChange={setConfirm}
          placeholder="Repeat it"
          type="password"
          name="confirm"
          autoComplete="new-password"
        />

        {error && (
          <div role="alert" style={{ fontSize: 12.5, color: color.danger, lineHeight: 1.5 }}>
            {error}
          </div>
        )}

        <AccentButton
          accent={accent}
          type="submit"
          disabled={busy}
          style={{ height: 38, width: '100%', borderRadius: 10, fontSize: 13.5 }}
        >
          {busy ? 'Saving…' : 'Set password and sign in'}
        </AccentButton>
      </form>

      <div style={{ fontSize: 11.5, color: color.textGhost, textAlign: 'center', lineHeight: 1.5 }}>
        Setting a new password signs you out everywhere else.
      </div>
    </Card>
  );
}
